import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

// Rides above the DS signup banner (z-index 200 in
// single_work_page/signup_banner.scss) so the dock reminder is never buried by
// it. Still mirrors the badges' footer-offset handshake read-only: observe
// .js-sticky-cta-footer's class attribute, never write to it. 96px is the
// footer height constant the badges also hardcode.
const Container = styled.div<{ footerOffset: boolean }>`
  position: fixed;
  right: var(--spacing-xs-16, 16px);
  bottom: var(--spacing-xs-16, 16px);
  z-index: 201;
  transition: bottom 0.2s ease;

  @media (prefers-reduced-motion: no-preference) {
    animation: dock-enter 0.28s ease-out both;
  }

  @keyframes dock-enter {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.footerOffset {
    bottom: calc(96px + var(--spacing-xs-16, 16px));
  }
`;

const PillBody = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xxs-8, 8px);
  max-width: 280px;
  padding: var(--spacing-xxs-8, 8px) var(--spacing-xs-16, 16px);
  background: var(--background-white, #ffffff);
  border: 1px solid var(--primary-brand, #0645b1);
  border-radius: var(--border-radius-radius-full, 999px);
  box-shadow: 0 4px 14px rgba(6, 69, 177, 0.28);
  cursor: pointer;

  @media (prefers-reduced-motion: no-preference) {
    animation: dock-pulse 2.4s ease-in-out infinite;
  }

  @keyframes dock-pulse {
    0%,
    100% {
      box-shadow: 0 4px 14px rgba(6, 69, 177, 0.28);
    }
    50% {
      box-shadow: 0 4px 20px rgba(6, 69, 177, 0.5);
    }
  }

  &:hover {
    animation-play-state: paused;
    box-shadow: 0 6px 22px rgba(6, 69, 177, 0.55);
  }
`;

const Thumbnail = styled.img`
  width: 24px;
  height: 32px;
  object-fit: cover;
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: var(--border-radius-radius-xs, 2px);
`;

const Message = styled.span`
  overflow: hidden;
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #26262b);
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const DismissButton = styled.button`
  position: absolute;
  top: -8px;
  right: -4px;
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--text-secondary, #6b6b76);
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: 50%;
  cursor: pointer;
`;

export type EdgePillProps = {
  message: string;
  thumbnailUrl?: string;
  onClick: () => void;
  onDismiss?: () => void;
};

function useFooterOffset(): boolean {
  const [footerOffset, setFooterOffset] = useState(false);

  useEffect(() => {
    const footer = document.querySelector('.js-sticky-cta-footer');
    if (!footer) return undefined;

    const update = () => setFooterOffset(!footer.classList.contains('hidden'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(footer, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return footerOffset;
}

export const EdgePill: React.FC<EdgePillProps> = ({
  message,
  thumbnailUrl,
  onClick,
  onDismiss,
}) => {
  const footerOffset = useFooterOffset();

  return (
    <Container footerOffset={footerOffset}>
      <PillBody type="button" onClick={onClick}>
        {thumbnailUrl && <Thumbnail src={thumbnailUrl} alt="" />}
        <Message>{message}</Message>
      </PillBody>
      {onDismiss && (
        <DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
          ✕
        </DismissButton>
      )}
    </Container>
  );
};
